import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { HTTP_STATUS } from "@base/lib/error";
import { isAxiosApiError } from "@base/lib/utils";
import { useProjectAnalyticsGroup } from "@base/model/hooks";
import type { DraftSaveResponse } from "@features/draft/model/schema";
import { useSaveDraftMutation } from "@features/draft/model/useDraftListQuery";
import { useDraftStore } from "@features/draft/model/useDraftStore";
import { useSyncDraftToForm } from "@features/draft/model/useSyncDraftToForm";
import { invalidateDailyLogSearch } from "@features/project-detail/model/dailyLogSearchQueries";
import {
  WriteDailyLogFunnel,
  dailyLogFormWithDraftToSubmitRequestSchema,
  useSubmitDailyLogMutation,
} from "@features/write-daily-log";
import { useDailyLogForm } from "@features/write-daily-log/model/form";
import type { DailyLogFormValues } from "@features/write-daily-log/model/schema";
import { useAutoSave } from "@features/write-daily-log/model/useAutoSave";
import { topLevelIssueFields, useDailyLogWriteTracker } from "@features/write-daily-log/model/useDailyLogWriteTracker";
import { SubmittingOverlay } from "@features/write-daily-log/ui/SubmittingOverlay";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";

const searchSchema = z.object({
  draftId: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/projects/$projectId/log/new")({
  validateSearch: searchSchema,
  component: CreateDailyLogPage,
});

function CreateDailyLogPage() {
  const { projectId } = Route.useParams();
  const { draftId: draftIdFromSearch } = Route.useSearch();
  const navigate = useNavigate();

  const draftId = useMemo(() => draftIdFromSearch ?? uuidv4(), [draftIdFromSearch]);
  const { draft, upsert, getDraft, remove } = useDraftStore(draftId, Number(projectId));
  const queryClient = useQueryClient();
  const saveDraftMutation = useSaveDraftMutation();
  const lastPopulatedVersion = useRef(-1);

  const { defaultValues, resolvedVersion, isInitialSyncPending } = useSyncDraftToForm(Number(projectId), draftId, {
    serverFallback: !!draftIdFromSearch,
  });

  const tracker = useDailyLogWriteTracker({ projectId, mode: "create", draftId });

  useProjectAnalyticsGroup(projectId);

  const submitDailyLogMutation = useSubmitDailyLogMutation(projectId, {
    onError: (err) => {
      tracker.trackSubmitFail({ reason: "api", errorCode: isAxiosApiError(err) ? err.response.data.code : undefined });

      if (isAxiosApiError(err)) {
        if (err.response.data.code === "DAILY_LOG_ALREADY_SUBMITTED") {
          toast.error("Daily Log for today already exists");
          return;
        }
      }

      toast.error("Failed to submit daily log");
    },
  });

  const handleSubmit = (values: DailyLogFormValues) => {
    tracker.trackSubmitStart();

    const result = dailyLogFormWithDraftToSubmitRequestSchema.safeParse({ ...values, draft_id: draftId });
    if (!result.success) {
      tracker.trackSubmitFail({ reason: "validation", failedFields: topLevelIssueFields(result.error) });
      toast.error("Please check your daily log fields.");
      return;
    }

    submitDailyLogMutation.mutate(result.data, {
      onSuccess: async (data) => {
        tracker.trackSubmitSuccess(values);

        const pId = Number(projectId);

        try {
          await remove();
        } catch {
          toast.error("Daily log was submitted, but the local draft could not be removed.");
        }

        queryClient.invalidateQueries({ queryKey: ["drafts", pId] });
        queryClient.removeQueries({ queryKey: ["drafts", pId, draftId] });
        queryClient.invalidateQueries({ queryKey: ["documents", "infinite", pId] });
        queryClient.invalidateQueries({ queryKey: ["documents", pId] });
        invalidateDailyLogSearch(queryClient, pId);

        navigate({
          to: "/projects/$projectId/log/$logId",
          params: { projectId, logId: data.id.toString() },
          replace: true,
        });
      },
    });
  };

  const form = useDailyLogForm({ onSubmit: handleSubmit });

  useEffect(() => {
    if (!defaultValues || resolvedVersion <= lastPopulatedVersion.current) return;
    lastPopulatedVersion.current = resolvedVersion;

    for (const [key, value] of Object.entries(defaultValues)) {
      form.setFieldValue(key as keyof DailyLogFormValues, value as never);
    }
  }, [defaultValues, resolvedVersion, form]);

  const handleLocalSave = useCallback(
    async (values: DailyLogFormValues) => {
      const currentDraft = await getDraft();

      if (!currentDraft) {
        EventLogger.track(CLIENT_EVENT.DRAFT_CREATED, {
          project_id: projectId,
          draft_id: draftId,
          source: draftIdFromSearch ? "resumed" : "new",
        });
      }

      await upsert({
        draft_id: draftId,
        version: currentDraft?.version ?? 0,
        content: values,
        updated_at: new Date().toISOString(),
      });
    },
    [draftId, draftIdFromSearch, projectId, getDraft, upsert],
  );

  const handleServerSave = useCallback(
    async (values: DailyLogFormValues) => {
      const currentDraft = await getDraft();
      saveDraftMutation.mutate(
        {
          projectId: Number(projectId),
          body: {
            draft_id: draftId,
            version: currentDraft?.version ?? 0,
            content: values,
          },
        },
        {
          onSuccess: (data) => {
            upsert(data);
          },
          onError: (error: unknown) => {
            if (isAxiosApiError(error)) {
              const { status, data } = error.response.data;

              if (status === HTTP_STATUS.CONFLICT) {
                EventLogger.track(CLIENT_EVENT.DRAFT_VERSION_CONFLICT, {
                  project_id: projectId,
                  draft_id: draftId,
                  local_version: currentDraft?.version ?? 0,
                  server_version: (data as { version?: number } | undefined)?.version ?? null,
                });

                toast.info("Your draft was updated on another device. Your changes will sync shortly.");

                if (data) {
                  upsert(data as unknown as DraftSaveResponse);
                }
                return;
              }
            }
          },
        },
      );
    },
    [draftId, projectId, getDraft, saveDraftMutation, upsert],
  );

  useAutoSave(form, {
    onLocalSave: handleLocalSave,
    onServerSave: handleServerSave,
  });

  const handleNavigateBack = () => {
    navigate({ to: "/projects/$projectId", params: { projectId }, search: { tab: "documents" } });
  };

  return (
    <div className="relative flex-1">
      <WriteDailyLogFunnel
        form={form}
        projectId={projectId}
        draftId={draftId}
        onSubmit={() => form.handleSubmit()}
        onNavigateBack={handleNavigateBack}
        onStepChange={tracker.trackStepView}
        isSaving={saveDraftMutation.isPending}
        lastUpdatedAt={saveDraftMutation.isError ? draft?.updated_at : saveDraftMutation.data?.updated_at}
        isInitialSyncPending={isInitialSyncPending}
      />
      {submitDailyLogMutation.isPending && <SubmittingOverlay />}
    </div>
  );
}
